import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppSidebar from '../components/common/AppSidebar';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/userService';
import toast from 'react-hot-toast';

const UserProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const inputRef = useRef(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    first_name: '',
    last_name: '',
    phone: '',
    bio: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) {
        navigate('/signin');
        return;
      }
      
      try {
        setIsLoading(true);
        const profileData = await userService.getCurrentUserProfile();
        setProfile(profileData);
        setFormData({
          username: profileData.username || '',
          email: profileData.email || '',
          name: profileData.name || '',
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          phone: profileData.phone || '',
          bio: profileData.bio || ''
        });
      } catch (error) {
        console.error('Error fetching user profile:', error);
        toast.error('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [user, navigate]);

  // Focus input when editing field changes
  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingField]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const startEditing = (fieldName) => {
    setEditingField(fieldName);
  };
  
  const cancelEditing = () => {
    // Reset the current field to its original value
    if (profile && editingField) {
      setFormData(prev => ({
        ...prev,
        [editingField]: profile[editingField] || ''
      }));
    }
    setEditingField(null);
  };
  
  const saveField = async (fieldName) => {
    if (!fieldName) return;
    
    setIsSaving(true);
    
    try {
      // Only send the field being edited
      const fieldData = { [fieldName]: formData[fieldName] };
      const updatedProfile = await userService.updateProfile(fieldData);
      
      // Update the profile with the new data
      setProfile(prev => ({
        ...prev,
        [fieldName]: formData[fieldName]
      }));
      
      setEditingField(null);
      toast.success(`${fieldName.replace('_', ' ')} updated successfully`);
      
      // Refresh the user context if available
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
      toast.error(`Failed to update ${fieldName.replace('_', ' ')}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveField(fieldName);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const getGreeting = () => {
    if (!profile) return "My Profile";
    
    const displayName = profile.name || 
      (profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : null);
    
    if (!displayName) return "My Profile";
    
    return `Hello, ${displayName}`;
  };
  
  const renderField = (fieldName, label, multiline = false) => {
    const isEditing = editingField === fieldName;
    const value = profile ? (profile[fieldName] || '') : '';
    
    return (
      <div className="user-profile-page__detail-item">
        <div className="user-profile-page__detail-header">
          <h3>{label}</h3>
          {!isEditing && (
            <button 
              onClick={() => startEditing(fieldName)}
              className="user-profile-page__edit-field-button"
              aria-label={`Edit ${label}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
              </svg>
            </button>
          )}
        </div>
        
        {isEditing ? (
          <div className="user-profile-page__field-edit">
            {multiline ? (
              <textarea
                ref={inputRef}
                name={fieldName}
                value={formData[fieldName]}
                onChange={handleInputChange}
                onKeyDown={(e) => handleKeyDown(e, fieldName)}
                rows="4"
                className="user-profile-page__edit-input"
              />
            ) : (
              <input
                ref={inputRef}
                type={fieldName === 'email' ? 'email' : fieldName === 'phone' ? 'tel' : 'text'}
                name={fieldName}
                value={formData[fieldName]}
                onChange={handleInputChange}
                onKeyDown={(e) => handleKeyDown(e, fieldName)}
                className="user-profile-page__edit-input"
              />
            )}
            <div className="user-profile-page__field-actions">
              <button 
                onClick={cancelEditing}
                className="user-profile-page__field-button user-profile-page__field-button--cancel"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                onClick={() => saveField(fieldName)}
                className="user-profile-page__field-button user-profile-page__field-button--save"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <p>{value || 'Not provided'}</p>
        )}
      </div>
    );
  };
  
  // Inside your main component, add this near the top to check for claim details
  useEffect(() => {
    // Check if we have claim details in localStorage right when the profile loads
    const enquiryDetails = localStorage.getItem('claimEnquiryDetails');
    const appointmentDetails = localStorage.getItem('claimAppointmentDetails');
    
    if (enquiryDetails || appointmentDetails) {
      // We have details to show - don't remove them yet, the ClaimSuccessDetails component will handle that
      console.log('Found claim details to display');
    }
  }, []);

  return (
    <div className="user-profile-page">
      <SEO 
        fallbackTitle="My Profile | Zahoot"
        fallbackDescription="View and manage your profile on Zahoot."
      />
      
      {/* Sidebar Navigation */}
      <AppSidebar userType={user?.role_name === 'lawyer' ? 'lawyer' : 'user'} />
      
      <div className="user-profile-page__content">
        <div className="user-profile-page__container">
          <div className="user-profile-page__header">
            <div className="user-profile-page__greeting">
              <h1>{getGreeting()}</h1>
              <p className="user-profile-page__welcome-text">
                Welcome to your personal profile. Here you can view and update your information.
              </p>
            </div>
          </div>
          
          <div className="user-profile-page__body">
            {isLoading ? (
              <div className="user-profile-page__loading">
                <div className="spinner"></div>
                <p>Loading profile information...</p>
              </div>
            ) : (
              profile && (
                <div className="user-profile-page__details">
                  {renderField('username', 'Username')}
                  {renderField('email', 'Email')}
                  {renderField('name', 'Display Name')}
                  {renderField('first_name', 'First Name')}
                  {renderField('last_name', 'Last Name')}
                  {renderField('phone', 'Phone Number')}
                  {renderField('bio', 'About Me', true)}
                  
                  <div className="user-profile-page__detail-item">
                    <h3>Account Type</h3>
                    <p>{profile.role_name || 'User'}</p>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .user-profile-page {
          min-height: 100vh;
          background: linear-gradient(180deg, #301555 0%, #411C73 50%, #1E0B38 100%);
          display: flex;
          flex-direction: row;
          position: relative;
        }
        
        .user-profile-page__content {
          flex-grow: 1;
          padding: 4rem 1rem;
          margin-left: 70px;
          width: calc(100% - 70px);
          display: flex;
          justify-content: center;
        }
        
        .user-profile-page__container {
          max-width: 600px;
          width: 100%;
          background: rgba(48, 21, 85, 0.4);
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(8px);
        }
        
        .user-profile-page__header {
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 1rem;
          border-bottom: 1px solid rgba(208, 188, 255, 0.2);
          padding-bottom: 1.5rem;
        }
        
        .user-profile-page__greeting {
          width: 100%;
        }
        
        .user-profile-page__greeting h1 {
          margin-bottom: 0.5rem;
          font-size: 2rem;
          font-weight: 700;
          color: white;
        }
        
        .user-profile-page__welcome-text {
          color: #d1bcff;
          margin-bottom: 1rem;
        }
        
        .user-profile-page__body {
          width: 100%;
        }
        
        .user-profile-page__detail-item {
          padding: 1rem 0;
          border-bottom: 1px solid rgba(208, 188, 255, 0.2);
        }
        
        .user-profile-page__detail-item:last-child {
          border-bottom: none;
        }
        
        .user-profile-page__detail-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }
        
        .user-profile-page__detail-header h3 {
          font-size: 0.9rem;
          color: #a492d1;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0;
        }
        
        .user-profile-page__edit-field-button {
          background: transparent;
          border: none;
          color: rgba(208, 188, 255, 0.6);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .user-profile-page__edit-field-button:hover {
          color: white;
          background: rgba(208, 188, 255, 0.1);
        }
        
        .user-profile-page__detail-item p {
          font-size: 1rem;
          color: white;
          margin: 0.5rem 0 0 0;
          min-height: 24px;
        }
        
        .user-profile-page__field-edit {
          margin-top: 0.5rem;
        }
        
        .user-profile-page__edit-input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid rgba(208, 188, 255, 0.3);
          border-radius: 6px;
          background: rgba(30, 11, 56, 0.6);
          color: white;
          font-size: 1rem;
          transition: all 0.2s ease;
        }
        
        .user-profile-page__edit-input:focus {
          outline: none;
          border-color: rgba(208, 188, 255, 0.8);
          box-shadow: 0 0 0 2px rgba(208, 188, 255, 0.2);
        }
        
        .user-profile-page__field-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.75rem;
          justify-content: flex-end;
        }
        
        .user-profile-page__field-button {
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .user-profile-page__field-button--save {
          background: linear-gradient(92.68deg, #D80364 0%, #F0654B 100%);
          color: white;
          border: none;
          box-shadow: 0 2px 6px rgba(216, 3, 100, 0.3);
        }
        
        .user-profile-page__field-button--save:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(216, 3, 100, 0.4);
        }
        
        .user-profile-page__field-button--cancel {
          background-color: transparent;
          color: white;
          border: 1px solid rgba(208, 188, 255, 0.4);
        }
        
        .user-profile-page__field-button--cancel:hover {
          background: rgba(208, 188, 255, 0.1);
        }
        
        .user-profile-page__loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          color: white;
        }
        
        .spinner {
          border: 3px solid rgba(208, 188, 255, 0.3);
          border-radius: 50%;
          border-top: 3px solid #D80364;
          width: 30px;
          height: 30px;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (min-width: 640px) {
          .user-profile-page__header {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }
          
          .user-profile-page__greeting {
            width: auto;
          }
        }
        
        @media (max-width: 768px) {
          .user-profile-page {
            flex-direction: column;
          }
          
          .user-profile-page__content {
            margin-left: 0;
            width: 100%;
            padding-top: 5rem;
          }
          
          .user-profile-page__container {
            padding: 1.5rem;
          }
          
          .user-profile-page__header {
            margin-bottom: 1rem;
            padding-bottom: 1rem;
            gap: 0.75rem;
          }
          
          .user-profile-page__greeting h1 {
            font-size: 1.75rem;
            margin-bottom: 0.25rem;
          }
          
          .user-profile-page__welcome-text {
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
          }
        }
        
        @media (min-width: 1025px) {
          .user-profile-page__content {
            margin-left: 110px;
            width: calc(100% - 110px);
          }
        }
      `}</style>
    </div>
  );
};

export default UserProfilePage; 